/**
 * ========================================
 * 📅 CALENDAR DATE UTILITIES
 * ========================================
 * 
 * This file contains utility functions for date manipulation and formatting
 * specifically designed for calendar functionality. Includes business logic
 * for working days, date ranges, and calendar-specific date operations.
 * 
 * Key Features:
 * - Date range calculations and validation
 * - Business hours and working days utilities
 * - Calendar-specific date formatting
 * - Timezone-aware date operations
 * - Comprehensive error handling
 * 
 * ========================================
 */

import type { CalendarDateRange } from './types.js';

// ========================================
// CONSTANTS
// ========================================

/**
 * Default business hours configuration
 */
export const DEFAULT_BUSINESS_HOURS = {
  start: '08:00',
  end: '17:00',
} as const;

/**
 * Default working days (Monday = 1, Sunday = 0)
 */
export const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5] as const;

/**
 * Weekend days (Saturday = 6, Sunday = 0)
 */
export const WEEKEND_DAYS = [0, 6] as const;

// ========================================
// DATE RANGE FUNCTIONS
// ========================================

/**
 * 📅 Get Date Range
 * 
 * Creates a CalendarDateRange object with additional metadata and validation.
 * Ensures the start date is before or equal to the end date and provides
 * useful properties for calendar operations.
 * 
 * @param start Start date of the range
 * @param end End date of the range
 * @param options Optional configuration for the range
 * @returns CalendarDateRange object with metadata
 * 
 * @example
 * ```typescript
 * const range = getDateRange(
 *   new Date('2024-01-01'),
 *   new Date('2024-01-31'),
 *   { label: 'January 2024', businessDaysOnly: true }
 * );
 * 
 * console.log(range.label); // 'January 2024'
 * console.log(range.businessDaysOnly); // true
 * ```
 */
export function getDateRange(
  start: Date,
  end: Date,
  options: {
    label?: string;
    isPreset?: boolean;
    presetId?: string;
    includeWeekends?: boolean;
    businessDaysOnly?: boolean;
  } = {}
): CalendarDateRange {
  // Validate dates
  if (!(start instanceof Date) || isNaN(start.getTime())) {
    throw new Error('Start date must be a valid Date object');
  }
  
  if (!(end instanceof Date) || isNaN(end.getTime())) {
    throw new Error('End date must be a valid Date object');
  }
  
  if (start > end) {
    throw new Error('Start date must be before or equal to end date');
  }

  // Normalize dates to start/end of day
  const normalizedStart = new Date(start);
  normalizedStart.setHours(0, 0, 0, 0);
  
  const normalizedEnd = new Date(end);
  normalizedEnd.setHours(23, 59, 59, 999);

  return {
    start: normalizedStart,
    end: normalizedEnd,
    label: options.label,
    isPreset: options.isPreset || false,
    presetId: options.presetId,
    includeWeekends: options.includeWeekends ?? true,
    businessDaysOnly: options.businessDaysOnly || false,
  };
}

/**
 * 📅 Get Preset Date Ranges
 * 
 * Returns commonly used date ranges for calendar filtering.
 * 
 * @returns Object containing preset date ranges
 */
export function getPresetDateRanges(): Record<string, CalendarDateRange> {
  const today = new Date();
  const startOfWeek = getStartOfWeek(today);
  const endOfWeek = getEndOfWeek(today);
  const startOfMonth = getStartOfMonth(today);
  const endOfMonth = getEndOfMonth(today);
  
  return {
    today: getDateRange(today, today, {
      label: 'Today',
      isPreset: true,
      presetId: 'today',
    }),
    
    thisWeek: getDateRange(startOfWeek, endOfWeek, {
      label: 'This Week',
      isPreset: true,
      presetId: 'thisWeek',
    }),
    
    thisMonth: getDateRange(startOfMonth, endOfMonth, {
      label: 'This Month',
      isPreset: true,
      presetId: 'thisMonth',
    }),
    
    next7Days: getDateRange(today, addDays(today, 6), {
      label: 'Next 7 Days',
      isPreset: true,
      presetId: 'next7Days',
    }),
    
    next30Days: getDateRange(today, addDays(today, 29), {
      label: 'Next 30 Days',
      isPreset: true,
      presetId: 'next30Days',
    }),
    
    lastWeek: getDateRange(
      addDays(startOfWeek, -7),
      addDays(endOfWeek, -7),
      {
        label: 'Last Week',
        isPreset: true,
        presetId: 'lastWeek',
      }
    ),
    
    lastMonth: getDateRange(
      getStartOfMonth(addMonths(today, -1)),
      getEndOfMonth(addMonths(today, -1)),
      {
        label: 'Last Month',
        isPreset: true,
        presetId: 'lastMonth',
      }
    ),
  };
}

// ========================================
// BUSINESS DAYS FUNCTIONS
// ========================================

/**
 * 📅 Is Business Day
 * 
 * Determines if a given date falls on a business day (Monday-Friday by default).
 * Can be configured with custom working days.
 * 
 * @param date The date to check
 * @param workingDays Array of working day numbers (0=Sunday, 1=Monday, etc.)
 * @returns True if the date is a business day
 * 
 * @example
 * ```typescript
 * const monday = new Date('2024-01-15'); // Monday
 * const saturday = new Date('2024-01-13'); // Saturday
 * 
 * console.log(isBusinessDay(monday)); // true
 * console.log(isBusinessDay(saturday)); // false
 * 
 * // Custom working days (including Saturday)
 * console.log(isBusinessDay(saturday, [1, 2, 3, 4, 5, 6])); // true
 * ```
 */
export function isBusinessDay(
  date: Date,
  workingDays: readonly number[] = DEFAULT_WORKING_DAYS
): boolean {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error('Date must be a valid Date object');
  }
  
  const dayOfWeek = date.getDay();
  return workingDays.includes(dayOfWeek);
}

/**
 * 📅 Get Business Hours
 * 
 * Returns the configured business hours for the organization.
 * In a real application, this might fetch from user preferences or organization settings.
 * 
 * @param customHours Optional custom business hours
 * @returns Object with start and end times in HH:MM format
 * 
 * @example
 * ```typescript
 * const hours = getBusinessHours();
 * console.log(hours); // { start: '08:00', end: '17:00' }
 * 
 * const customHours = getBusinessHours({ start: '09:00', end: '18:00' });
 * console.log(customHours); // { start: '09:00', end: '18:00' }
 * ```
 */
export function getBusinessHours(customHours?: {
  start: string;
  end: string;
}): { start: string; end: string } {
  if (customHours) {
    // Validate time format
    if (!isValidTimeFormat(customHours.start) || !isValidTimeFormat(customHours.end)) {
      throw new Error('Business hours must be in HH:MM format');
    }
    return customHours;
  }
  
  return { ...DEFAULT_BUSINESS_HOURS };
}

/**
 * 📅 Get Business Days in Range
 * 
 * Calculates the number of business days within a given date range.
 * 
 * @param start Start date of the range
 * @param end End date of the range
 * @param workingDays Array of working day numbers
 * @returns Number of business days in the range
 */
export function getBusinessDaysInRange(
  start: Date,
  end: Date,
  workingDays: readonly number[] = DEFAULT_WORKING_DAYS
): number {
  if (start > end) {
    throw new Error('Start date must be before or equal to end date');
  }
  
  let count = 0;
  const current = new Date(start);
  
  while (current <= end) {
    if (isBusinessDay(current, workingDays)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return count;
}

// ========================================
// DATE FORMATTING FUNCTIONS
// ========================================

/**
 * 📅 Format Date for Calendar
 * 
 * Formats a date for display in calendar components. Handles both
 * Date objects and ISO string inputs with proper error handling.
 * 
 * @param date Date to format (Date object or ISO string)
 * @param options Formatting options
 * @returns Formatted date string
 * 
 * @example
 * ```typescript
 * const date = new Date('2024-01-15T10:30:00Z');
 * 
 * console.log(formatDateForCalendar(date)); // '2024-01-15'
 * console.log(formatDateForCalendar(date, { includeTime: true })); // '2024-01-15 10:30'
 * console.log(formatDateForCalendar(date, { format: 'short' })); // 'Jan 15, 2024'
 * ```
 */
export function formatDateForCalendar(
  date: string | Date,
  options: {
    format?: 'iso' | 'short' | 'long' | 'relative';
    includeTime?: boolean;
    timeZone?: string;
    locale?: string;
  } = {}
): string {
  let dateObj: Date;
  
  // Parse input date
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else if (date instanceof Date) {
    dateObj = date;
  } else {
    throw new Error('Date must be a Date object or ISO string');
  }
  
  if (isNaN(dateObj.getTime())) {
    throw new Error('Invalid date provided');
  }
  
  const {
    format = 'iso',
    includeTime = false,
    timeZone = 'UTC',
    locale = 'en-US',
  } = options;
  
  try {
    switch (format) {
      case 'iso':
        if (includeTime) {
          return dateObj.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
        }
        return dateObj.toISOString().slice(0, 10); // YYYY-MM-DD
        
      case 'short':
        return new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          ...(includeTime && {
            hour: '2-digit',
            minute: '2-digit',
          }),
          timeZone,
        }).format(dateObj);
        
      case 'long':
        return new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          weekday: 'long',
          ...(includeTime && {
            hour: '2-digit',
            minute: '2-digit',
          }),
          timeZone,
        }).format(dateObj);
        
      case 'relative':
        return formatRelativeDate(dateObj);
        
      default:
        throw new Error(`Unsupported format: ${format}`);
    }
  } catch (error) {
    console.error('Date formatting error:', error);
    return dateObj.toISOString().slice(0, 10); // Fallback to ISO date
  }
}

/**
 * 📅 Parse Calendar Date
 * 
 * Parses various date string formats into a Date object.
 * Handles common calendar input formats with validation.
 * 
 * @param dateStr Date string to parse
 * @returns Parsed Date object
 * 
 * @example
 * ```typescript
 * console.log(parseCalendarDate('2024-01-15')); // Date object
 * console.log(parseCalendarDate('2024-01-15T10:30:00Z')); // Date object
 * console.log(parseCalendarDate('01/15/2024')); // Date object
 * ```
 */
export function parseCalendarDate(dateStr: string): Date {
  if (!dateStr || typeof dateStr !== 'string') {
    throw new Error('Date string is required');
  }

  let date: Date;

  // IMPORTANT: Handle YYYY-MM-DD format as LOCAL time, not UTC
  // new Date('2024-01-15') parses as UTC midnight, which can appear as
  // the previous day in timezones behind UTC (e.g., US timezones)
  const isoDateOnly = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoDateOnly) {
    const [, year, month, day] = isoDateOnly;
    // Create date using local timezone by specifying year, month, day
    date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  } else {
    // For full ISO strings with time component, use standard parsing
    date = new Date(dateStr);
  }

  // If parsing failed, try common formats
  if (isNaN(date.getTime())) {
    // Try MM/DD/YYYY format
    const mmddyyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyy) {
      const [, month, day, year] = mmddyyyy;
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }

    // Try DD/MM/YYYY format
    if (isNaN(date.getTime())) {
      const ddmmyyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (ddmmyyyy) {
        const [, day, month, year] = ddmmyyyy;
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    }
  }

  if (isNaN(date.getTime())) {
    throw new Error(`Unable to parse date string: ${dateStr}`);
  }

  return date;
}

/**
 * 🗓️ Parse Local Date (Timezone-Safe)
 *
 * Parses a date string as a LOCAL date, not UTC. Handles multiple formats:
 * - "2024-01-23" (date only)
 * - "2024-01-23T00:00:00" (local datetime)
 * - "2024-01-23T00:00:00.000Z" (UTC datetime)
 * - "2024-01-23T00:00:00+00:00" (timezone datetime)
 *
 * IMPORTANT: This function ALWAYS extracts just the date portion (YYYY-MM-DD)
 * and returns a Date at midnight LOCAL time. This prevents timezone issues
 * where dates appear as the previous day.
 *
 * @param dateStr Date string (any ISO format, or null/undefined)
 * @returns Date object at midnight local time, or null if input is null/undefined
 *
 * @example
 * ```typescript
 * // All of these return January 23rd at midnight LOCAL time:
 * parseLocalDate('2024-01-23')                    // Jan 23 local
 * parseLocalDate('2024-01-23T00:00:00.000Z')      // Jan 23 local (NOT Jan 22!)
 * parseLocalDate('2024-01-23T05:30:00+05:30')     // Jan 23 local
 * parseLocalDate(null)                            // null
 * ```
 */
export function parseLocalDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) {
    return null;
  }

  // Extract just the YYYY-MM-DD portion from any ISO format
  // This regex matches the date part at the start of:
  // - "2024-01-23" (date only)
  // - "2024-01-23T..." (any datetime format)
  const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    // Create date at midnight LOCAL time
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }

  // Fallback: try to parse and extract local date components
  // This handles edge cases like timestamps without the standard format
  try {
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      // Extract local date components to avoid timezone shifts
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  } catch {
    // Ignore parsing errors
  }

  return null;
}

/**
 * Is this timestamp a calendar date with no real time of day? True for a bare
 * 'YYYY-MM-DD', for UTC midnight ('…T00:00:00Z' / '…T00:00:00+00:00' — how a
 * row saved as a bare date comes back from a timestamptz column) and for
 * offset-less local midnight. Any other time of day is a real timestamp.
 */
export function isDateOnlyTimestamp(dateStr: string | null | undefined): boolean {
  if (!dateStr) {
    return false;
  }
  return /^\d{4}-\d{2}-\d{2}(?:[T ]00:00(?::00(?:\.0+)?)?(?:Z|[+-]00(?::?00)?)?)?$/.test(dateStr);
}

/**
 * Parse an activity date (e.g. scheduled_date) as the moment it starts:
 * date-only values (see isDateOnlyTimestamp) → midnight LOCAL time on that
 * date; real timestamps → that instant.
 */
export function parseActivityDate(dateStr: string): Date {
  if (isDateOnlyTimestamp(dateStr)) {
    return parseLocalDate(dateStr) as Date;
  }
  return parseCalendarDate(dateStr);
}

/**
 * Value for an <input type="date"> from a stored date or timestamp. The input
 * only accepts 'YYYY-MM-DD' and renders a full timestamp
 * ('2026-10-20T00:00:00+00:00') blank. Uses the day parseActivityDate reads,
 * so UTC-midnight and local-midnight rows both show their saved day.
 */
export function toDateInputValue(dateStr: string | null | undefined): string | null {
  if (!dateStr) {
    return null;
  }
  let date: Date;
  try {
    date = parseActivityDate(dateStr);
  } catch {
    return null;
  }
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Get the start of the week (Monday) for a given date
 */
function getStartOfWeek(date: Date): Date {
  const start = new Date(date);
  const day = start.getDay();
  const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  start.setDate(diff);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Get the end of the week (Sunday) for a given date
 */
function getEndOfWeek(date: Date): Date {
  const end = new Date(date);
  const day = end.getDay();
  const diff = end.getDate() + (7 - day);
  end.setDate(diff);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Get the start of the month for a given date
 */
function getStartOfMonth(date: Date): Date {
  const start = new Date(date);
  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Get the end of the month for a given date
 */
function getEndOfMonth(date: Date): Date {
  const end = new Date(date);
  end.setMonth(end.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Add days to a date
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add business days to a date (skips weekends)
 *
 * Adds the specified number of business days to a date, skipping
 * Saturday (6) and Sunday (0). Handles both positive and negative offsets.
 *
 * @param date Starting date
 * @param businessDays Number of business days to add (can be negative)
 * @param workingDays Optional array of working day numbers (default: Mon-Fri = [1,2,3,4,5])
 * @returns New date after adding business days
 *
 * @example
 * ```typescript
 * // Friday + 1 business day = Monday (skips weekend)
 * const friday = new Date('2024-01-12');
 * addBusinessDays(friday, 1); // 2024-01-15 (Monday)
 *
 * // Monday - 1 business day = Friday (skips weekend)
 * const monday = new Date('2024-01-15');
 * addBusinessDays(monday, -1); // 2024-01-12 (Friday)
 * ```
 */
export function addBusinessDays(
  date: Date,
  businessDays: number,
  workingDays: readonly number[] = DEFAULT_WORKING_DAYS
): Date {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error('Date must be a valid Date object');
  }

  const result = new Date(date);

  if (businessDays === 0) {
    return result;
  }

  const direction = businessDays > 0 ? 1 : -1;
  let remaining = Math.abs(businessDays);

  while (remaining > 0) {
    result.setDate(result.getDate() + direction);
    if (workingDays.includes(result.getDay())) {
      remaining--;
    }
  }

  return result;
}

/**
 * Add months to a date
 */
function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Validate time format (HH:MM)
 */
function isValidTimeFormat(time: string): boolean {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
}

/**
 * Format relative date (e.g., "Today", "Yesterday", "2 days ago")
 */
function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays === -1) {
    return 'Tomorrow';
  } else if (diffDays > 1 && diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < -1 && diffDays > -7) {
    return `In ${Math.abs(diffDays)} days`;
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * 📅 Is Date in Range
 * 
 * Checks if a date falls within a given date range.
 * 
 * @param date Date to check
 * @param range Date range to check against
 * @returns True if date is within range
 */
export function isDateInRange(date: Date, range: CalendarDateRange): boolean {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new Error('Date must be a valid Date object');
  }
  
  return date >= range.start && date <= range.end;
}

/**
 * 📅 Get Days in Range
 * 
 * Returns an array of all dates within a given range.
 * 
 * @param range Date range
 * @param options Options for filtering days
 * @returns Array of dates in the range
 */
export function getDaysInRange(
  range: CalendarDateRange,
  options: {
    businessDaysOnly?: boolean;
    workingDays?: readonly number[];
  } = {}
): Date[] {
  const days: Date[] = [];
  const current = new Date(range.start);
  const { businessDaysOnly = false, workingDays = DEFAULT_WORKING_DAYS } = options;
  
  while (current <= range.end) {
    if (!businessDaysOnly || isBusinessDay(current, workingDays)) {
      days.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }
  
  return days;
}